type AddressParts = {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
};

/**
 * Builds the display address from the stored parts.
 *
 * Mirrors the API's composeFullAddress. Parts are preferred because they are
 * unambiguous; addressLine1 is only split when a record predates the parts
 * being captured correctly, which is lossy and exists purely for those rows.
 */
export const composeFullAddress = (address?: AddressParts | null): string => {
  const line1 = (address?.addressLine1 || '').trim();
  // Records written before Address 2 existed mirrored the formatted address
  // into both fields; those carry no unit and must not repeat the street.
  const rawLine2 = (address?.addressLine2 || '').trim();
  const line2 = rawLine2 === line1 ? '' : rawLine2;

  const city = (address?.city || '').trim();
  const state = (address?.state || '').trim();
  const zip = (address?.zip || '').trim();
  const country = (address?.country || '').trim();

  // Legacy rows stored the county as the city ("chester county"), the
  // unabbreviated state ("pennsylvania"), and 00000 as a placeholder zip.
  // Composing from those reads worse than the formatted address Google gave
  // us, so they are treated as having no usable parts. The edit form writes
  // the same placeholder zip when Places returns none, so this guard must
  // match the API's byte for byte — both values render the same address.
  const hasTrustworthyParts =
    Boolean(city) &&
    /^[A-Za-z]{2}$/.test(state) &&
    Boolean(zip) &&
    zip !== '00000';

  // Fall back to what Google formatted, inserting the unit after the street.
  if (!hasTrustworthyParts) {
    if (!line2) {
      return line1;
    }

    if (!line1) {
      return line2;
    }

    const [first, ...rest] = line1.split(',').map((part) => part.trim());

    return [[first, line2].filter(Boolean).join(' '), ...rest]
      .filter(Boolean)
      .join(', ');
  }

  // The street is the first segment of addressLine1; the rest of that string
  // duplicates city/state/zip, which we now hold discretely.
  const street = line1.split(',')[0].trim();

  // The unit follows the street on the same segment, and state and zip share
  // one: "1889 E Maule Ave Suite J, Las Vegas, NV 89119, USA" (ClickUp
  // 86bcgvh8k).
  const streetAndUnit = [street, line2].filter(Boolean).join(' ');
  const region = [state, zip].filter(Boolean).join(' ');

  return [streetAndUnit, city, region, country].filter(Boolean).join(', ');
};

export default composeFullAddress;
